import React from 'react';
import { MapPin } from 'lucide-react';
import { humanizeRecurrenceDays, fmtClock } from './shuttleFormat';

// Peoria Charter quote card rendered inline in the customer chat. Extracted out of the shared
// ChatMessage.js so charter UI lives under src/charter/. Renders a SIMPLE customer estimate — the
// price range, what's included (base charter rate · fuel surcharge · extra fees), and the trip miles.
// A recurring shuttle (tripType "shuttle") is priced by the hour, not the mile, so its branch swaps
// the route arrow to ↔, adds a Schedule block (dates · days · service window), and replaces the
// Distance row with the hours math ("{hrs} hrs/day × {days} × {coaches}").
// DOT/feasibility internals are dispatcher-only and never shown here.

const PENDING_LABELS = {
  destinationDetail: 'the exact venue or drop-off spot',
  pickupAddress: 'the pickup street address',
};

// Qualitative labels for the range-mode "Includes" list (feesIncluded keys from the agents' card
// payload). The customer sees WHICH components priced in — never their dollar amounts, which are
// stripped from the payload so the range can't be reverse-engineered.
const FEE_INCLUDED_LABELS = {
  base_rate: 'base charter rate',
  fuel_surcharge: 'fuel surcharge',
  extra_fees: 'extra fees',
};

function feeIncludedLabel(key, quote) {
  const base = FEE_INCLUDED_LABELS[key] || key.replace(/_/g, ' ');
  if (key === 'fuel_surcharge' && quote.fuelSurchargePercent != null) {
    return `${base} (${Number(quote.fuelSurchargePercent)}%)`;
  }
  if (key === 'driver_hotel' && quote.overnightNights > 0) {
    return `${base} (${quote.overnightNights} night${quote.overnightNights !== 1 ? 's' : ''})`;
  }
  if (key === 'pickup_routing' && quote.pickupRoutingMiles) {
    return `${base} (${quote.pickupRoutingMiles} mi)`;
  }
  return base;
}

// A price-band bound: whole dollars stay clean ("$850"), anything else keeps cents ("$847.13").
function bandMoney(v) {
  const n = Number(v);
  return `$${Number.isInteger(n)
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Free-text stops ("stop at Dekalb Oasis; then the arena") -> a compact "A · B" route label. Mirrors
// the API's stop parsing (split on ';', newline, or " then "; strip a leading "stop at"/"via"). A
// "none"/"no stops" sentinel renders nothing.
function cleanStops(stops) {
  if (!stops || typeof stops !== 'string') return '';
  const t = stops.trim();
  if (!t || ['none', 'no', 'n/a', 'straight through'].includes(t.toLowerCase())) return '';
  return t
    .split(/\s*(?:;|\n|\bthen\b)\s*/i)
    .map((s) => s.replace(/^\s*(?:stops?\s+at|stop|via)\s+/i, '').trim())
    .filter(Boolean)
    .join(' · ');
}

// Format an ISO datetime for the trip summary (mirrors formatDateTime in ChatMessage.js). A date-only
// value (YYYY-MM-DD, no time) is parsed as a LOCAL date so it doesn't slip to the previous evening in
// US timezones, and renders without a time. Non-parseable / already-human text is returned as-is.
function fmtDateTime(dt) {
  if (!dt || typeof dt !== 'string') return '';
  const s = dt.trim();
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
  const d = new Date(dateOnly ? `${s}T00:00:00` : s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(dateOnly ? {} : { hour: 'numeric', minute: '2-digit', hour12: true }),
  });
}

// ── Shuttle helpers ─────────────────────────────────────────────────────────────────────────────

// Parse a YYYY-MM-DD service date as a LOCAL date (same trick as fmtDateTime) so it doesn't slip to
// the previous evening in US timezones. Returns null when not parseable.
function parseServiceDate(s) {
  if (!s || typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s.trim())) return null;
  const d = new Date(`${s.trim()}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

// Service dates: a single day gets the full "Sat, Sep 19, 2026"; a range reads "Jun 2 – Jun 13, 2026"
// (year once when shared, on both bounds when the range crosses New Year's).
function fmtServiceDates(start, end) {
  const sd = parseServiceDate(start);
  if (!sd) return start || '';
  const single = !end || end === start;
  if (single) {
    return sd.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }
  const ed = parseServiceDate(end);
  if (!ed) return `${start} – ${end}`;
  if (sd.getFullYear() === ed.getFullYear()) {
    return `${sd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${ed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${ed.getFullYear()}`;
  }
  return `${sd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${ed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

// Recurrence cadence ("MON,TUE,…" -> "Mon–Fri") + the 12-hour clock come from the shared
// shuttleFormat helpers so this card and the dispatcher's ShuttleSummary render them identically.

// A clock range that collapses a shared AM/PM onto the end ("7:00 – 9:30 AM"; "7:00 AM – 6:30 PM").
function fmtWindowRange(start, end) {
  const a = fmtClock(start);
  const b = fmtClock(end);
  const suffix = a.slice(-3);
  return suffix === b.slice(-3) && / (AM|PM)$/.test(a) ? `${a.slice(0, -3)} – ${b}` : `${a} – ${b}`;
}

// The schedule line: single day -> "Sat, Sep 19, 2026"; a range appends the cadence + day count:
// "Jun 2 – Jun 13, 2026 · Mon–Fri · 10 service days".
function shuttleScheduleLine(quote) {
  const single = !quote.serviceEndDate || quote.serviceEndDate === quote.serviceStartDate;
  const dates = fmtServiceDates(quote.serviceStartDate, quote.serviceEndDate);
  if (single) return dates;
  const parts = [dates, humanizeRecurrenceDays(quote.recurrenceDays)];
  if (quote.serviceDays != null) {
    parts.push(`${quote.serviceDays} service day${Number(quote.serviceDays) !== 1 ? 's' : ''}`);
  }
  return parts.join(' · ');
}

// The service-window line: continuous -> "7:00 AM – 6:30 PM"; split -> the out + return blocks with
// the released-in-between note (the split's billing basis, mirrored from the Java formula).
function shuttleWindowLine(quote) {
  if (quote.splitWindows && quote.outWindowEnd && quote.backWindowStart) {
    return `Out ${fmtWindowRange(quote.windowStart, quote.outWindowEnd)} · Return ${fmtWindowRange(quote.backWindowStart, quote.windowEnd)} · bus released in between`;
  }
  return fmtWindowRange(quote.windowStart, quote.windowEnd);
}

// The hourly-pricing math shown in place of the Distance row — the same factors Java multiplies.
function shuttleServiceLine(quote) {
  const days = Number(quote.serviceDays || 1);
  const coaches = Number(quote.coachCount || 1);
  return `${Number(quote.billableHoursPerDay)} hrs/day × ${days} day${days !== 1 ? 's' : ''} × ${coaches} coach${coaches !== 1 ? 'es' : ''}`;
}

// A place is "resolved" once it's an exact street address. A blank (deferred/PENDING) address or a
// bare venue/campus name with no street number still Needs exact location — the itinerary-first flow
// quotes with TBD locations, then the rider resolves each here.
function isBareVenue(s) {
  return !!s && !/\d/.test(String(s));   // no street number -> not yet an exact address
}

// One pickup/drop-off row with a 4-state resolution status, so the card communicates where each
// location stands instead of just pass/fail:
//   resolved    — an exact street address: green ✓, nothing to do.
//   approximate — a city or bare venue: prices as a preliminary estimate; [Refine] opens the form.
//   pending     — deferred/TBD (Java pendingFields): [Add] opens the form to fill it in.
// The workflow never blocks — approximate/pending are informative, not errors. Resolve/Refine/Add all
// reuse the one consolidated route form.
function LocationRow({ label, value, status, onResolve }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-peoria-muted mr-0.5">{label}</span>
      {value ? <span>{value}</span> : null}
      {status === 'resolved' && value ? (
        <span className="text-green-700" title="resolved">✓</span>
      ) : status === 'approximate' ? (
        <>
          <span className="text-11 text-amber-700" title="approximate — preliminary estimate">≈ Approximate — preliminary estimate</span>
          {onResolve && (
            <button type="button" onClick={onResolve} className="text-11 text-amber-700 underline hover:opacity-80">
              Refine
            </button>
          )}
        </>
      ) : (
        <>
          <span className="text-11 text-amber-700">⚠ Needs exact location</span>
          {onResolve && (
            <button type="button" onClick={onResolve} className="text-11 text-amber-700 underline hover:opacity-80">
              Add
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Consolidated multi-leg TRIP card: one trip header (duration, miles, drive hours, driver requirement,
// hotels, combined band) followed by each leg rendered as its own sub-card (recursing into
// CharterQuoteCard, so every leg reuses the full per-leg body incl. its own overnight + Edit-route).
// The per-leg entries already carry `legLabel` ("Leg N of M"), so onEditRoute(N) opens the right leg.
function CharterTripCard({ quote, onEditRoute }) {
  const hasBand = quote.tripTotalMin != null && quote.tripTotalMax != null;
  const hotels = Array.isArray(quote.hotels) ? quote.hotels.filter((h) => h && h.city) : [];
  return (
    <div className="bg-peoria-surface border border-peoria-border rounded-card p-5 my-2">
      <div className="flex items-center justify-between mb-1 gap-2">
        <span className="text-base font-semibold text-peoria-text">
          Multi-Day Charter Trip{quote.quoteNumber ? <span className="font-mono"> {quote.quoteNumber}</span> : null}
        </span>
        {quote.tripDuration ? (
          <span className="text-11 font-medium px-2.5 py-0.5 rounded-full border bg-peoria-red/10 text-peoria-red border-peoria-red/30">
            {quote.tripDuration}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-13 text-peoria-text mb-1">
        {quote.passengerCount ? <span>{quote.passengerCount} passengers</span> : null}
        {quote.totalMiles != null && <span>~{Number(quote.totalMiles).toFixed(0)} mi total</span>}
        {quote.totalDriveHours != null && <span>~{Number(quote.totalDriveHours).toFixed(1)} h driving</span>}
        {quote.legs ? <span>{quote.legs.length} legs</span> : null}
      </div>
      {hotels.length > 0 && (
        <div className="text-13 text-peoria-text mb-1">
          Overnight: {hotels.map((h) => `${h.city}${h.nights != null ? ` (${h.nights} night${h.nights !== 1 ? 's' : ''})` : ''}`).join(' · ')}
        </div>
      )}
      {/* Multi-day round trip: the driver is lodged between the outbound and return trips. Show the
          nights (and the estimate when we have one) so the driver-hotel cost is visible, not buried. */}
      {quote.driverHotelNights > 0 && (
        <div className="text-13 text-peoria-text mb-1">
          Driver hotel: {quote.driverHotelNights} night{quote.driverHotelNights !== 1 ? 's' : ''}
          {quote.driverHotelEstimate != null
            ? ` · ~$${Number(quote.driverHotelEstimate).toFixed(0)} (included in the estimate)`
            : ' (included in the estimate)'}
        </div>
      )}
      {quote.pricePending ? (
        <div className="text-15 font-semibold text-peoria-text mt-2">Trip estimate: pending</div>
      ) : hasBand ? (
        <div className="mt-2">
          <div className="text-11 uppercase tracking-brand text-peoria-muted">Estimated trip range</div>
          <div className="text-lg font-semibold text-peoria-text">{bandMoney(quote.tripTotalMin)} – {bandMoney(quote.tripTotalMax)}</div>
        </div>
      ) : null}
      {/* DOT feasibility is dispatcher-only — never shown on the customer quote card. */}
      <div className="mt-3 space-y-2">
        {(quote.legs || []).map((leg, i) => (
          // Per-leg "Resolve" links still work (onEditRoute), but the single "Edit itinerary" button
          // below is the ONE edit affordance — no redundant per-leg buttons.
          <CharterQuoteCard key={i} quote={leg} onEditRoute={onEditRoute} hideEditButton />
        ))}
      </div>
      {/* Between-the-trips note (coach disposition / hotels) — the rider's free-text answer, shown at
          the bottom of the itinerary so dispatch sees the driver-stay + lodging plan. */}
      {quote.coachDisposition ? (
        <div className="mt-3 rounded-card border border-peoria-border bg-peoria-surface2/40 p-3">
          <div className="text-10 font-semibold uppercase tracking-brand text-peoria-muted mb-1">
            Between the trips
          </div>
          <div className="text-13 text-peoria-text">{quote.coachDisposition}</div>
        </div>
      ) : null}
      {onEditRoute && (
        <button
          type="button"
          onClick={() => onEditRoute('multi')}
          className="mt-3 inline-flex items-center gap-1.5 text-13 font-semibold text-peoria-red bg-peoria-red/10 hover:bg-peoria-red/20 border border-peoria-red/30 rounded-full px-3 py-1.5 transition-colors"
        >
          <MapPin size={15} strokeWidth={2.25} /> Edit itinerary
        </button>
      )}
    </div>
  );
}

export function CharterQuoteCard({ quote, onEditRoute, hideEditButton }) {
  // Consolidated trip payload → the trip layout (each leg recurses back through this component).
  if (Array.isArray(quote.legs)) {
    return <CharterTripCard quote={quote} onEditRoute={onEditRoute} />;
  }
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  const isPartial = quote.quoteStatus === 'PARTIAL';
  const pending = (quote.pendingFields || []).map((f) => PENDING_LABELS[f] || f);
  // Range mode: the quote carries a customer price band — show ONLY the band (no exact total, no
  // dollar line items). Band-less payloads (older agents/Java) keep the legacy exact rendering.
  const hasBand = quote.totalMin != null && quote.totalMax != null;
  // Price-pending: no road mileage was available, so the quote carries NO money figures at all —
  // render a placeholder instead of the band/total (and never the legacy "$0.00").
  const pricePending = !!quote.pricePending;
  const isRoundTrip = quote.tripType === 'round_trip';
  // Recurring shuttle: a bus looping pickup ↔ dropoff over a daily service window, priced by the
  // hour (see the shuttle helpers above). Extra pickup points ride stopsStructured (purpose "pickup").
  const isShuttle = quote.tripType === 'shuttle';
  const shuttlePickups = isShuttle && Array.isArray(quote.stopsStructured)
    ? quote.stopsStructured.filter((s) => s && s.purpose === 'pickup' && s.location)
    : [];
  // The return leg's route (round trip): the rider's confirmed ride-home route, falling back to the
  // reverse of the outbound so the there-and-back always reads correctly even before they edit it.
  const returnFrom = quote.returnPickupAddress || quote.destinationDetail;
  const returnTo = quote.returnDestinationDetail || quote.pickupAddress;
  // Per-location resolution status: pending (deferred/TBD, Java lists it in pendingFields) or a bare
  // venue name still Needs exact location; an exact street address is resolved (✓).
  const pf = quote.pendingFields || [];
  const pickupPending = pf.includes('pickupAddress');
  const destPending = pf.includes('destinationDetail');
  // 4-state per-location status: an exact street address is resolved; a bare venue/city is an
  // approximate (preliminary-estimate) location; a deferred/blank field is pending.
  const locStatus = (val, isPending) =>
    isPending || !val ? 'pending' : (isBareVenue(val) ? 'approximate' : 'resolved');
  const pickupStatus = locStatus(quote.pickupAddress, pickupPending);
  const destStatus = locStatus(quote.destinationDetail, destPending);
  // Every edit — a "Resolve" link or the "Edit itinerary" button — opens the ONE consolidated route
  // form (the arg is ignored by CustomerApp, which always opens 'multi'; kept explicit for clarity).
  const resolvePickup = onEditRoute ? () => onEditRoute('multi') : null;
  return (
    <div className="bg-peoria-surface border border-peoria-border rounded-card p-5 my-2">
      <div className="flex items-center justify-between mb-1 gap-2">
        <span className="text-base font-semibold text-peoria-text">
          {quote.legLabel ? <span className="text-11 font-semibold text-peoria-red uppercase tracking-brand mr-1.5">{quote.legLabel}</span> : null}
          Charter Quote{quote.quoteNumber ? <span className="font-mono"> {quote.quoteNumber}</span> : null}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {isPartial && (
            <span className="text-11 font-medium px-2.5 py-0.5 rounded-full border bg-amber-500/10 text-amber-700 border-amber-500/30">
              Estimate — details pending
            </span>
          )}
          {quote.tripType && (
            <span className="text-11 font-medium px-2.5 py-0.5 rounded-full border bg-peoria-red/10 text-peoria-red border-peoria-red/30">
              {quote.tripType === 'shuttle' ? 'Shuttle' : quote.tripType === 'round_trip' ? 'Round trip' : 'One-way'}
            </span>
          )}
        </div>
      </div>
      <div className="text-13 text-peoria-text">
        {quote.groupName ? <span className="font-medium">{quote.groupName} · </span> : null}
        {/* Shuttle = a loop, so the route reads pickup ↔ dropoff (two-way), not a one-way →. */}
        {quote.pickupCity}, {quote.pickupState} {isShuttle ? '↔' : '→'} {quote.dropoffCity}, {quote.dropoffState}
        {/* Round trip: show the return leg so the there-and-back route is visible, not just A → B. */}
        {quote.tripType === 'round_trip' && <> → {quote.pickupCity}, {quote.pickupState}</>}
        {quote.passengerCount != null && <span className="text-peoria-muted"> · {quote.passengerCount} passengers</span>}
        {quote.numWheelchairs > 0 && <span className="text-peoria-muted"> · {quote.numWheelchairs} wheelchair {quote.numWheelchairs === 1 ? 'space' : 'spaces'} (lift-equipped)</span>}
        {quote.coachCount > 1 && <span className="text-peoria-muted"> · {quote.coachCount} coaches</span>}
      </div>
      {/* Edit-in-context: re-open the map popup (pre-filled) to change the route. Only the LATEST
          quote card gets this — CustomerApp passes onEditRoute for that message only. A round trip is
          two legs of travel, so it gets a separate edit for the outbound and the return route. */}
      {onEditRoute && !hideEditButton && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onEditRoute('multi')}
            className="inline-flex items-center gap-1.5 text-13 font-semibold text-peoria-red bg-peoria-red/10 hover:bg-peoria-red/20 border border-peoria-red/30 rounded-full px-3 py-1.5 transition-colors"
          >
            <MapPin size={15} strokeWidth={2.25} /> Edit itinerary
          </button>
        </div>
      )}
      {/* Intermediate stops are routed through as real waypoints (the distance/price include the
          detour); surface them so the rider sees the multi-stop route reflected on the card.
          Prefer the STRUCTURED stops (location · purpose · dwell) when present; else the "Via …" line. */}
      {isShuttle ? (shuttlePickups.length > 0 && (
        // Shuttle: the structured stops are EXTRA pickup points on the loop, not en-route waypoints.
        <div className="text-11 text-peoria-muted mt-0.5">
          Also picks up at: {shuttlePickups.map((s) => s.location).join(' · ')}
        </div>
      )) : Array.isArray(quote.stopsStructured) && quote.stopsStructured.length > 0 ? (
        <div className="text-11 text-peoria-muted mt-0.5">
          Via {quote.stopsStructured.map((s, i) => (
            <span key={i}>
              {i > 0 && ' · '}
              {s.location}
            </span>
          ))}
        </div>
      ) : cleanStops(quote.stops) && (
        <div className="text-11 text-peoria-muted mt-0.5">Via {cleanStops(quote.stops)}</div>
      )}

      {/* Shuttle schedule — the service dates (+ cadence + service-day count for a range) and the
          daily window (continuous, or the split out/return blocks with the bus released in between). */}
      {isShuttle && (quote.serviceStartDate || (quote.windowStart && quote.windowEnd)) && (
        <div className="mt-3 pt-3 border-t border-peoria-border">
          <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Schedule</span>
          <div className="mt-1 space-y-0.5 text-13 text-peoria-text">
            {quote.serviceStartDate && (
              <div><span className="text-peoria-muted mr-1.5">Dates</span>{shuttleScheduleLine(quote)}</div>
            )}
            {quote.windowStart && quote.windowEnd && (
              <div><span className="text-peoria-muted mr-1.5">Window</span>{shuttleWindowLine(quote)}</div>
            )}
          </div>
        </div>
      )}

      {/* Trip summary: recap everything the rider entered — depart/return date-time, the pickup and
          destination detail, and the group's overnight lodging (its own line, distinct from the
          driver-hotel price line below). Each row renders only when we have that value. */}
      {(quote.departureDateTime || quote.pickupAddress || quote.destinationDetail
        || quote.returnDateTime || quote.overnightStay || pickupPending || destPending) && (
        <div className="mt-3 pt-3 border-t border-peoria-border">
          <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Trip</span>
          <div className="mt-1 space-y-0.5 text-13 text-peoria-text">
            {/* Shuttle: NO Depart/Return rows — the payload's pickupDateTime/returnDateTime mirror the
                daily window the Schedule block already shows, and "Depart/Return" would misread the
                loop as a single out-and-back trip. */}
            {!isShuttle && quote.departureDateTime && (
              <div><span className="text-peoria-muted mr-1.5">Depart</span>{fmtDateTime(quote.departureDateTime)}</div>
            )}
            {(quote.pickupAddress || pickupPending) && (
              <LocationRow label="From" value={quote.pickupAddress} status={pickupStatus} onResolve={resolvePickup} />
            )}
            {(quote.destinationDetail || destPending) && (
              <LocationRow label="To" value={quote.destinationDetail} status={destStatus} onResolve={resolvePickup} />
            )}
            {!isShuttle && quote.returnDateTime && (
              <div><span className="text-peoria-muted mr-1.5">Return</span>{fmtDateTime(quote.returnDateTime)}</div>
            )}
            {/* Return leg (round trip): its own pickup/drop-off so the ride home reads as a real
                second leg — defaults to the reverse of the outbound until the rider edits it. */}
            {isRoundTrip && returnFrom && (
              <div><span className="text-peoria-muted mr-1.5">Return from</span>{returnFrom}</div>
            )}
            {isRoundTrip && returnTo && (
              <div><span className="text-peoria-muted mr-1.5">Return to</span>{returnTo}</div>
            )}
            {isRoundTrip && cleanStops(quote.returnStops) && (
              <div><span className="text-peoria-muted mr-1.5">Return via</span>{cleanStops(quote.returnStops)}</div>
            )}
            {quote.overnightStay && (
              <div>
                <span className="text-peoria-muted mr-1.5">Overnight stay</span>{quote.overnightStay}
                {quote.overnightStayNights && (
                  <span className="text-peoria-muted"> ({quote.overnightStayNights} night{String(quote.overnightStayNights) === '1' ? '' : 's'})</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Flight block — only on an airport trip (arrival pickup / departure drop-off). Shows the
          flight the rider gave + the live status when a tracking vendor is wired (UNKNOWN is hidden). */}
      {(quote.airportDirection || quote.airline || quote.flightNumber || quote.flightDateTime || quote.terminal) && (
        <div className="mt-3 pt-3 border-t border-peoria-border">
          <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Flight</span>
          <div className="mt-1 space-y-0.5 text-13 text-peoria-text">
            {quote.airportDirection && (
              <div><span className="text-peoria-muted mr-1.5">Type</span>{quote.airportDirection === 'departure' ? 'Departure drop-off' : 'Arrival pickup'}</div>
            )}
            {(quote.airline || quote.flightNumber) && (
              <div><span className="text-peoria-muted mr-1.5">Flight</span>{[quote.airline, quote.flightNumber].filter(Boolean).join(' ')}</div>
            )}
            {quote.flightDateTime && (
              <div><span className="text-peoria-muted mr-1.5">{quote.airportDirection === 'departure' ? 'Departs' : 'Lands'}</span>{fmtDateTime(quote.flightDateTime)}</div>
            )}
            {quote.terminal && (
              <div><span className="text-peoria-muted mr-1.5">Terminal</span>{quote.terminal}</div>
            )}
            {quote.meetGreet && (
              <div><span className="text-peoria-muted mr-1.5">Meet-greet</span>{quote.meetGreet}</div>
            )}
            {quote.flightStatus && quote.flightStatus !== 'UNKNOWN' && (
              <div><span className="text-peoria-muted mr-1.5">Status</span>{quote.flightStatus}</div>
            )}
          </div>
        </div>
      )}

      {/* Event logistics — venue schedule + how the coach handles the gap (waits vs shuttles). */}
      {(quote.venueTiming || quote.multiplePickupTimes || quote.waitingWindowMode) && (
        <div className="mt-3 pt-3 border-t border-peoria-border">
          <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Event</span>
          <div className="mt-1 space-y-0.5 text-13 text-peoria-text">
            {quote.venueTiming && <div><span className="text-peoria-muted mr-1.5">Venue</span>{quote.venueTiming}</div>}
            {quote.multiplePickupTimes && <div><span className="text-peoria-muted mr-1.5">Pickups</span>{quote.multiplePickupTimes}</div>}
            {quote.waitingWindowMode && quote.waitingWindowMode !== 'UNKNOWN' && (
              <div><span className="text-peoria-muted mr-1.5">Between movements</span>{quote.waitingWindowMode === 'WAIT' ? 'coach waits on-site' : 'coach shuttles back and forth'}</div>
            )}
          </div>
        </div>
      )}

      {/* Equipment / cargo — what the group is bringing + whether a trailer is needed. */}
      {(quote.equipment || quote.needsTrailer) && (
        <div className="mt-3 pt-3 border-t border-peoria-border">
          <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Equipment</span>
          <div className="mt-1 space-y-0.5 text-13 text-peoria-text">
            {quote.equipment && <div><span className="text-peoria-muted mr-1.5">Gear</span>{quote.equipment}</div>}
            {quote.needsTrailer && <div><span className="text-peoria-muted mr-1.5">Trailer</span>{quote.needsTrailer}</div>}
          </div>
        </div>
      )}

      {/* Accessibility — wheelchair spaces (lift-equipped) + any notes the rider gave. */}
      {(quote.numWheelchairs > 0 || quote.accessibilityNotes) && (
        <div className="mt-3 pt-3 border-t border-peoria-border">
          <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Accessibility</span>
          <div className="mt-1 space-y-0.5 text-13 text-peoria-text">
            {quote.numWheelchairs > 0 && (
              <div><span className="text-peoria-muted mr-1.5">Wheelchair</span>{quote.numWheelchairs} lift-equipped {quote.numWheelchairs === 1 ? 'space' : 'spaces'}</div>
            )}
            {quote.accessibilityNotes && <div><span className="text-peoria-muted mr-1.5">Notes</span>{quote.accessibilityNotes}</div>}
          </div>
        </div>
      )}

      {/* DOT feasibility is dispatcher-only — never shown on the customer quote card. */}

      <div className="flex items-baseline justify-between py-3 mt-3 border-y border-peoria-border">
        <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">
          {pricePending || hasBand ? 'Estimated range' : 'Estimated total'}
        </span>
        {pricePending ? (
          <span className="text-2xl font-bold text-peoria-muted font-mono">Pending</span>
        ) : hasBand ? (
          <span className="text-2xl font-bold text-peoria-text font-mono">
            {bandMoney(quote.totalMin)} – {bandMoney(quote.totalMax)} <span className="text-sm font-medium text-peoria-muted">{quote.currency}</span>
          </span>
        ) : (
          <span className="text-3xl font-bold text-peoria-text font-mono">
            {money(quote.totalAmount)} <span className="text-sm font-medium text-peoria-muted">{quote.currency}</span>
          </span>
        )}
      </div>

      {pricePending ? (
        <p className="mt-3 text-11 text-peoria-muted leading-relaxed">
          We're confirming the road mileage for this route — a Peoria Charter rep will firm up your
          price shortly. Everything else about your trip is captured below and you can still send it in.
        </p>
      ) : hasBand ? (
        <div className="mt-3 space-y-1.5">
          {Array.isArray(quote.feesIncluded) && quote.feesIncluded.length > 0 && (
            <div className="text-11 leading-relaxed">
              <span className="text-peoria-muted">Includes </span>
              <span className="text-peoria-text">{quote.feesIncluded.map((k) => feeIncludedLabel(k, quote)).join(' · ')}</span>
            </div>
          )}
          {quote.hotelIncludedInQuote === false && quote.overnightNights > 0 && (
            <div className="text-11 text-peoria-muted">
              Driver hotel ({quote.overnightNights} night{quote.overnightNights !== 1 ? 's' : ''}) — you arrange
            </div>
          )}
          {/* Shuttle: money never touches miles — swap the Distance row for the hourly math the band
              is built from, plus the informational loop distance when routing resolved one. */}
          {isShuttle ? (
            <>
              {quote.billableHoursPerDay != null && (
                <div className="flex justify-between text-11">
                  <span className="text-peoria-muted">Service</span>
                  <span className="font-mono text-peoria-text">{shuttleServiceLine(quote)}</span>
                </div>
              )}
              {quote.loopMiles != null && (
                <div className="flex justify-between text-11">
                  <span className="text-peoria-muted">Loop</span>
                  <span className="font-mono text-peoria-text">≈ {Number(quote.loopMiles).toFixed(0)} mi</span>
                </div>
              )}
            </>
          ) : quote.distanceMiles != null && (
            <div className="flex justify-between text-11">
              <span className="text-peoria-muted">Distance</span>
              <span className="font-mono text-peoria-text">
              {quote.distanceEstimated
                ? `~${Number(quote.distanceMiles).toFixed(0)} mi (estimated)`
                : `${Number(quote.distanceMiles).toFixed(0)} mi`}
            </span>
            </div>
          )}
        </div>
      ) : (
      <div className="mt-3 space-y-1.5">
        {quote.netRate != null && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Base rate</span>
            <span className="font-mono text-peoria-text">{money(quote.netRate)}</span>
          </div>
        )}
        {quote.fuelSurchargeAmount != null && Number(quote.fuelSurchargeAmount) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Fuel surcharge ({Number(quote.fuelSurchargePercent)}%)</span>
            <span className="font-mono text-peoria-text">{money(quote.fuelSurchargeAmount)}</span>
          </div>
        )}
        {quote.alcoholFee != null && Number(quote.alcoholFee) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Alcohol contract fee</span>
            <span className="font-mono text-peoria-text">{money(quote.alcoholFee)}</span>
          </div>
        )}
        {/* Driver hotel + who's responsible for it. Included -> the billed line (marked "included");
            rider-arranged -> a no-cost line so the responsibility is explicit either way. */}
        {quote.driverHotelEstimate != null && Number(quote.driverHotelEstimate) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Driver hotel ({quote.overnightNights} night{quote.overnightNights !== 1 ? 's' : ''})<span className="text-peoria-muted"> · included</span></span>
            <span className="font-mono text-peoria-text">{money(quote.driverHotelEstimate)}</span>
          </div>
        )}
        {quote.hotelIncludedInQuote === false && quote.overnightNights > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Driver hotel ({quote.overnightNights} night{quote.overnightNights !== 1 ? 's' : ''})<span className="text-peoria-muted"> · you arrange</span></span>
            <span className="font-mono text-peoria-muted">—</span>
          </div>
        )}
        {quote.airportFee != null && Number(quote.airportFee) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Airport fee</span>
            <span className="font-mono text-peoria-text">{money(quote.airportFee)}</span>
          </div>
        )}
        {quote.trailerFee != null && Number(quote.trailerFee) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Cargo trailer</span>
            <span className="font-mono text-peoria-text">{money(quote.trailerFee)}</span>
          </div>
        )}
        {quote.accessibilityFee != null && Number(quote.accessibilityFee) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Lift-equipped coach</span>
            <span className="font-mono text-peoria-text">{money(quote.accessibilityFee)}</span>
          </div>
        )}
        {/* Tolls — estimated tolls folded into totalAmount by Java as a first-class component
            (0/null = no toll line). Render it so the visible breakdown reconciles to the total. */}
        {quote.tollsSurcharge != null && Number(quote.tollsSurcharge) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Tolls</span>
            <span className="font-mono text-peoria-text">{money(quote.tollsSurcharge)}</span>
          </div>
        )}
        {/* Pickup routing — origin-side miles collecting the group across staggered pickups, folded
            into totalAmount (0/null = none / billing off). Rendered so the visible breakdown reconciles. */}
        {quote.pickupRoutingCharge != null && Number(quote.pickupRoutingCharge) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Pickup routing{quote.pickupRoutingMiles ? ` (${quote.pickupRoutingMiles} mi)` : ''}</span>
            <span className="font-mono text-peoria-text">{money(quote.pickupRoutingCharge)}</span>
          </div>
        )}
        {/* Parking / permit — dispatcher-set parking/permit fee folded into the total (0/null = none).
            First-class response field, rendered like the other fee lines so the sum reconciles. */}
        {quote.parkingPermitFee != null && Number(quote.parkingPermitFee) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Parking / permit</span>
            <span className="font-mono text-peoria-text">{money(quote.parkingPermitFee)}</span>
          </div>
        )}
        {/* Minimum fare adjustment — when the priced components fall below the operator's floor, the
            Java quote surfaces the top-up as a first-class field (0/null = no floor). Render it so the
            visible breakdown (base rate + fees + this line) reconciles to the Estimated total above. */}
        {quote.minFareAdjustment != null && Number(quote.minFareAdjustment) > 0 && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Minimum fare adjustment</span>
            <span className="font-mono text-peoria-text">{money(quote.minFareAdjustment)}</span>
          </div>
        )}
        {quote.distanceMiles != null && (
          <div className="flex justify-between text-11">
            <span className="text-peoria-muted">Distance</span>
            <span className="font-mono text-peoria-text">
              {quote.distanceEstimated
                ? `~${Number(quote.distanceMiles).toFixed(0)} mi (estimated)`
                : `${Number(quote.distanceMiles).toFixed(0)} mi`}
            </span>
          </div>
        )}
      </div>
      )}

      {quote.tripNotes && (
        <div className="mt-3 pt-3 border-t border-peoria-border">
          <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Notes / special requests</span>
          <p className="text-13 text-peoria-text mt-1 leading-relaxed">{quote.tripNotes}</p>
        </div>
      )}

      {isPartial && pending.length > 0 && (
        <p className="mt-3 text-11 text-amber-700 leading-relaxed">
          This is a ballpark — it firms up once we have {pending.join(' and ')}.
        </p>
      )}

      {/* The DOT/placeholder pricing disclaimer is dispatcher-only and stripped from the customer card. */}

      {/* NOTE: the post-quote follow-up prompt travels IN the card payload (quote.followupPrompt) for
          robust transport, but it is rendered OUTSIDE the card — as an ordinary trailing bot message —
          by ChatMessage (see charterFollowup there). It must NOT render inside the card. */}
    </div>
  );
}

export default CharterQuoteCard;
